<template>
    <div :class="['flex flex-col', isCompact ? 'text-[12px]' : '']">
        <div class="flex justify-between items-center">
            <div class="flex items-center space-x-1">
                <button :class="[
                    'px-1 border-player-contrast border-[2px] rounded-[6px] font-bold focus:outline-none focus:ring-2 focus:ring-player-accent',
                    isCompact ? 'h-[16px] text-[6px]' : 'w-[24px] h-[20px] text-[10px]',
                    'text-player-contrast'
                ]" @click="changePlaybackRate()" :aria-label="playbackSpeedLabel" aria-live="polite">
                    {{ currentRate }}x
                </button>
                <button
                    v-if="hasTranscription"
                    :class="[
                        'px-1 border-[2px] rounded-[6px] font-bold flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-player-accent',
                        isCompact ? 'h-[16px] text-[6px] ml-1' : 'w-[24px] h-[20px] text-[10px] ml-2',
                        ccActive ? 'border-player-accent' : 'border-player-contrast',
                        'text-player-contrast'
                    ]"
                    :aria-pressed="ccActive"
                    @click="emit('toggle-cc')"
                    :title="__('Show/Hide transcript')"
                    :aria-label="__('Show/Hide transcript')"
                >
                    CC
                </button>
            </div>

            <div class="flex items-center">
                <!-- back 10s button -->
                <button @click="emit('back-seconds', 10)" class="hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-player-accent" :class="isCompact ? 'mr-2' : 'mr-6'" :title="__('Back 10 seconds')" :aria-label="__('Back 10 seconds')">
                    <IconRewind :class="isCompact ? 'w-[16px] h-[16px]' : 'w-[32px] h-[32px]'" />
                </button>

                <!-- play button -->
                <button v-if="!playing" @click="emit('play')" class="aspect-square hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-player-accent" :title="__('Play')" :aria-label="__('Play')">
                    <IconPlay :class="isCompact ? 'w-[28px] h-[28px]' : 'w-[57px] h-[57px]'" />
                </button>

                <!-- pause button -->
                <button v-else @click=" emit('pause')" class="aspect-square hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-player-accent" :title="__('Pause')" :aria-label="__('Pause')">
                    <IconPause :class="isCompact ? 'w-[28px] h-[28px]' : 'w-[57px] h-[57px]'" />
                </button>

                <!-- skip 10s button -->
                <button @click="emit('skip-seconds', 10)" class="hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-player-accent" :class="isCompact ? 'ml-2' : 'ml-6'" :title="__('Skip 10 seconds')" :aria-label="__('Skip 10 seconds')">
                    <IconForward :class="isCompact ? 'w-[16px] h-[16px]' : 'w-[32px] h-[32px]'" />
                </button>
            </div>

            <div class="flex items-center">
                <!-- mute button -->
                <button v-if="!muted" @click="emit('mute')" class="hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-player-accent" :title="__('Mute')" :aria-label="__('Mute')">
                    <IconMute :class="isCompact ? 'w-[12px] h-[12px]' : 'w-[24px] h-[24px]'" />
                </button>

                <!-- unmute button -->
                <button v-else @click="emit('unmute')" class="hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-player-accent" :title="__('Unmute')" :aria-label="__('Unmute')">
                    <IconUnmute :class="isCompact ? 'w-[12px] h-[12px]' : 'w-[24px] h-[24px]'" />
                </button>

                <ShareButton
                    v-if="showShare"
                    :seek="seek"
                    :episode-hash-id="episodeHashId"
                    :compact="isCompact"
                />
            </div>
        </div>

        <div class="flex flex-row justify-center space-x-2 mt-1">
        </div>
    </div>
</template>

<script setup>

import __, {themeVar} from "../helpers";
import IconPlay from "./IconPlay.vue";
import IconPause from "./IconPause.vue";
import IconRewind from "./IconRewind.vue";
import IconForward from "./IconForward.vue";
import IconMute from "./IconMute.vue";
import IconUnmute from "./IconUnmute.vue";
import ShareButton from "./ShareButton.vue";
import { computed, ref } from "vue";

const props = defineProps({
    playing: Boolean,
    muted: Boolean,
    ccActive: Boolean,
    hasTranscription: Boolean,
    compact: Boolean,
    showShare: Boolean,
    seek: {
        type: Number,
        default: 0,
    },
    episodeHashId: {
        type: String,
        default: null,
    },
})

const isCompact = computed(() => props.compact || props.ccActive)

const emit = defineEmits(['play', 'pause', 'back-seconds', 'skip-seconds', 'mute', 'unmute', 'playback-rate', 'toggle-cc'])

const playbackRates = [1, 1.5, 2]
const currentRate = ref(1)

const playbackSpeedLabel = computed(() => {
    return `${__('Playback speed')}, ${currentRate.value}x`;
});

function changePlaybackRate() {
    currentRate.value = playbackRates[(playbackRates.indexOf(currentRate.value) + 1) % playbackRates.length]

    emit('playback-rate', currentRate.value)
}

</script>
